import { Icon } from '@iconify/react';
import { styled } from '@linaria/react';
import { Menu } from '@mantine/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import type { TooltipContentProps } from 'recharts/types/component/Tooltip';
import { getPhaseColor } from '../artifact-colors';
import { useIsExporting } from '../artifact-export-context';
import type { ArtifactComponentProps } from '../artifact-registry';
import {
  PHASE_LABELS,
  PHASE_ORDER,
  buildScopedFlowByPhaseQuickLinks,
  getChartPhases,
  getInitialMetric,
  getInitialWorkPeriodIndex,
  isWorkPeriodsPayload,
  nullifyFutureChartData,
  type FlowByPhaseMetric,
} from './flow-by-phase-artifact.helpers';

/* ── Types ─────────────────────────────────────────────── */

interface WorkPeriodChartData {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  chart_data: Array<Record<string, string | number | null>>;
}

interface FlowByPhasePayload {
  work_periods: WorkPeriodChartData[];
}

/* ── Component ─────────────────────────────────────────── */

/**
 * Renders the Flora chat artifact view for backend-provided Flow-by-Phase data.
 *
 * @param props - Standard Flora artifact component props. `initialState` (when
 *   provided by the export route) seeds `selectedIndex` and `metric`. See
 *   `ArtifactComponentProps` JSDoc for the recognized keys.
 * @returns Flow-by-phase stacked-area chart with phase toggles.
 */
export function FlowByPhaseArtifact({ data, config, onQuickLinksChange, initialState }: ArtifactComponentProps) {
  const payload = useMemo(() => {
    if (isWorkPeriodsPayload(data)) return data as unknown as FlowByPhasePayload;
    return null;
  }, [data]);

  const isExporting = useIsExporting();

  const workPeriods = payload?.work_periods ?? [];

  const [selectedIndex, setSelectedIndex] = useState(() => getInitialWorkPeriodIndex(initialState));
  const [metric, setMetric] = useState<FlowByPhaseMetric>(() => getInitialMetric(initialState));
  const [visiblePhases, setVisiblePhases] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(PHASE_ORDER.map((p) => [p, true])),
  );

  // Clamp `selectedIndex` against the current `workPeriods.length`. The seed
  // from `initialState.workPeriodIndex` (or a stale value left over from a
  // previous payload) can otherwise drive `currentWP` to undefined and render
  // the "Invalid work period selection" empty state despite valid data.
  useEffect(() => {
    setSelectedIndex((index) => Math.min(index, Math.max(workPeriods.length - 1, 0)));
  }, [workPeriods.length]);

  const currentWP = workPeriods[selectedIndex];

  // Rebuild quick links scoped to the currently selected work period.
  useEffect(() => {
    if (!onQuickLinksChange) return;
    if (!currentWP) {
      onQuickLinksChange([]);
      return;
    }

    onQuickLinksChange(buildScopedFlowByPhaseQuickLinks(data, currentWP, config));
  }, [onQuickLinksChange, currentWP, config, data]);

  /** Discover which phases actually appear in the chart data. */
  const phases = useMemo(() => getChartPhases(currentWP?.chart_data ?? []), [currentWP]);

  /** For in-progress work periods, nullify phase values for dates after today. */
  const chartData = useMemo(() => nullifyFutureChartData(currentWP?.chart_data ?? []), [currentWP]);

  const togglePhase = useCallback((phaseId: string) => {
    setVisiblePhases((prev) => ({ ...prev, [phaseId]: !prev[phaseId] }));
  }, []);

  const goPrev = useCallback(() => {
    setSelectedIndex((i) => Math.max(0, i - 1));
  }, []);

  const goNext = useCallback(() => {
    setSelectedIndex((i) => Math.min(workPeriods.length - 1, i + 1));
  }, [workPeriods.length]);

  if (!payload || workPeriods.length === 0) {
    return <EmptyState>No flow-by-phase data available</EmptyState>;
  }

  if (!currentWP) {
    return <EmptyState>Invalid work period selection</EmptyState>;
  }

  const hasManyWPs = workPeriods.length > 1;

  return (
    <ChartContainer>
      {/* ── Header row ── */}
      <HeaderRow>
        <HeaderLeft>
          {hasManyWPs && (
            <NavBtn onClick={goPrev} disabled={selectedIndex === 0} aria-label="Previous work period">
              <Icon icon="material-symbols:chevron-left" width={16} height={16} />
            </NavBtn>
          )}

          {hasManyWPs ? (
            <Menu shadow="sm" radius="md" position="bottom-start" offset={4} withinPortal={false}>
              <Menu.Target>
                <WPDropdownBtn type="button" aria-label="Select work period">
                  <WPName>{currentWP.name || `Work Period ${selectedIndex + 1}`}</WPName>
                  <Icon icon="material-symbols:expand-more" width={14} height={14} />
                </WPDropdownBtn>
              </Menu.Target>
              <Menu.Dropdown>
                {workPeriods.map((wp, idx) => (
                  <Menu.Item
                    key={wp.id}
                    onClick={() => setSelectedIndex(idx)}
                    rightSection={
                      idx === selectedIndex ? <Icon icon="material-symbols:check" width={14} height={14} /> : undefined
                    }
                    style={{ fontSize: '13px' }}
                  >
                    {wp.name || `Work Period ${idx + 1}`}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
          ) : (
            <WPName>{currentWP.name || 'Work Period'}</WPName>
          )}

          {hasManyWPs && (
            <NavBtn onClick={goNext} disabled={selectedIndex === workPeriods.length - 1} aria-label="Next work period">
              <Icon icon="material-symbols:chevron-right" width={16} height={16} />
            </NavBtn>
          )}

          {currentWP.start_date && currentWP.end_date && (
            <DateRange>
              {currentWP.start_date} – {currentWP.end_date}
            </DateRange>
          )}
        </HeaderLeft>

        <Menu shadow="sm" radius="md" position="bottom-end" offset={4} withinPortal={false}>
          <Menu.Target>
            <MetricBtn type="button" aria-label="Select metric">
              {metric === 'tasks' ? 'Tasks' : 'Points'}
              <Icon icon="material-symbols:expand-more" width={14} height={14} />
            </MetricBtn>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              onClick={() => setMetric('tasks')}
              rightSection={
                metric === 'tasks' ? <Icon icon="material-symbols:check" width={14} height={14} /> : undefined
              }
              style={{ fontSize: '13px' }}
            >
              Tasks
            </Menu.Item>
            <Menu.Item
              onClick={() => setMetric('points')}
              rightSection={
                metric === 'points' ? <Icon icon="material-symbols:check" width={14} height={14} /> : undefined
              }
              style={{ fontSize: '13px' }}
            >
              Points
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </HeaderRow>

      {/* ── Chart ── */}
      <div style={{ width: '100%', height: '280px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} tickLine={false} />
            <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
            <Tooltip
              content={(props) => <ChartTooltip {...props} phaseOrder={PHASE_ORDER} visiblePhases={visiblePhases} />}
            />
            {/* Render ALL areas in reverse order (backlog on top), use hide prop for visibility.
                Always rendering all areas keeps stacking order stable when toggling phases. */}
            {[...phases].reverse().map((phaseId) => {
              const color = getPhaseColor(phaseId, PHASE_ORDER.indexOf(phaseId));
              const dataKey = `${metric}_${phaseId}`;
              return (
                <Area
                  key={phaseId}
                  type="monotone"
                  dataKey={dataKey}
                  name={PHASE_LABELS[phaseId] ?? phaseId}
                  stackId="1"
                  stroke={color}
                  fill={color}
                  fillOpacity={0.6}
                  hide={!visiblePhases[phaseId]}
                  isAnimationActive={!isExporting}
                />
              );
            })}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* ── Legend ── */}
      <LegendWrap>
        {phases.map((phaseId) => {
          const color = getPhaseColor(phaseId, PHASE_ORDER.indexOf(phaseId));
          const checked = visiblePhases[phaseId];
          return (
            <LegendItem key={phaseId}>
              <HiddenCheckbox type="checkbox" checked={checked} onChange={() => togglePhase(phaseId)} />
              <CustomCheckbox $color={color} $checked={checked}>
                {checked && <Icon icon="material-symbols:check" width={10} height={10} />}
              </CustomCheckbox>
              <LegendLabel $dimmed={!checked}>{PHASE_LABELS[phaseId] ?? phaseId}</LegendLabel>
            </LegendItem>
          );
        })}
      </LegendWrap>
    </ChartContainer>
  );
}

/* ── Custom Tooltip ────────────────────────────────────── */

function ChartTooltip({
  active,
  payload,
  label,
  phaseOrder,
  visiblePhases,
}: TooltipContentProps<ValueType, NameType> & { phaseOrder: string[]; visiblePhases: Record<string, boolean> }) {
  if (!active || !payload?.length) return null;

  const sorted = [...payload]
    .filter((entry) => {
      const phaseKey = String(entry.dataKey).replace(/^(tasks|points)_/, '');
      return visiblePhases[phaseKey];
    })
    .sort((a, b) => {
      const aKey = String(a.dataKey).replace(/^(tasks|points)_/, '');
      const bKey = String(b.dataKey).replace(/^(tasks|points)_/, '');
      return phaseOrder.indexOf(aKey) - phaseOrder.indexOf(bKey);
    });

  return (
    <TooltipContainer>
      <TooltipLabel>{label}</TooltipLabel>
      {sorted.map((entry) => (
        <TooltipRow key={entry.name}>
          <TooltipSwatch style={{ backgroundColor: entry.color }} />
          <span>{entry.name}</span>
          <TooltipValue>{entry.value}</TooltipValue>
        </TooltipRow>
      ))}
    </TooltipContainer>
  );
}

/* ── Styled components ─────────────────────────────────── */

const ChartContainer = styled.div`
  width: 100%;
`;

const EmptyState = styled.div`
  padding: 24px;
  text-align: center;
  color: var(--ink-lighter);
  font-size: 13px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0 4px 10px;
  flex-wrap: wrap;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const NavBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--ink-base);
  cursor: pointer;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background: rgba(0, 0, 0, 0.06);
  }

  &:disabled {
    opacity: 0.3;
    cursor: default;
  }
`;

const WPDropdownBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  font-size: 13px;
  color: var(--ink-dark);
  font-weight: 500;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }
`;

const WPName = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: var(--ink-dark);
`;

const DateRange = styled.span`
  font-size: 11px;
  color: var(--ink-lighter);
  margin-left: 6px;
`;

const MetricBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  font-size: 12px;
  color: var(--ink-base);

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }
`;

const LegendWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
  padding: 8px 4px 0;
`;

const LegendItem = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  user-select: none;
  padding: 2px 4px;
  border-radius: 4px;
  transition: background 0.15s;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }
`;

const HiddenCheckbox = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
`;

const CustomCheckbox = styled.span<{ $color: string; $checked: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 3px;
  border: 1.5px solid ${({ $color }) => $color};
  background-color: ${({ $checked, $color }) => ($checked ? $color : 'transparent')};
  color: #fff;
  flex-shrink: 0;
  transition: background-color 0.15s, border-color 0.15s;
`;

const LegendLabel = styled.span<{ $dimmed: boolean }>`
  font-size: 12px;
  color: var(--ink-base);
  opacity: ${({ $dimmed }) => ($dimmed ? 0.4 : 1)};
  transition: opacity 0.15s;
`;

const TooltipContainer = styled.div`
  background: var(--flora-chart-tooltip-bg);
  border: var(--flora-chart-tooltip-border);
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 12px;
  color: var(--flora-chart-tooltip-text);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

const TooltipLabel = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
`;

const TooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 1px 0;
`;

const TooltipSwatch = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 2px;
  flex-shrink: 0;
`;

const TooltipValue = styled.span`
  margin-left: auto;
  font-weight: 600;
`;
